import React, { useState, useMemo, useEffect } from "react";
import { CanEdit, useIsReadOnlyManager } from "@/shared/readOnlyManager";
import { useTable, usePagination, useSortBy, useRowSelect } from "react-table";
import { CircularProgress } from "@material-ui/core";
import IndeterminateCheckbox from "@/shared/components/form/IndeterminateCheckbox";
import {
  Table,
  Row,
  Col,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { connect } from "react-redux";
import { TABLE_COLUMNS } from "./Columns";
import ReactTablePagination from "@/shared/components/table/components/ReactTablePagination";
import apiTableService from "@/services/apiTableService";
import { useDispatch } from "react-redux";
import { TableSkeleton } from "@/shared/components/Skeletons";
import axios from "axios";
import { sendFlashMessage } from "@/redux/actions/flashActions";

const queryPageSize = 10;

const UnclaimedAwardsTable = ({ organization, program }) => {
  const [unclaimedAwards, setUnclaimedAwards] = useState(null);
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const readOnly = useIsReadOnlyManager();

  const SELECTION_COLUMN = {
    id: "selection",
    Header: ({ getToggleAllPageRowsSelectedProps }) => (
      <div>
        <IndeterminateCheckbox {...(getToggleAllPageRowsSelectedProps ? getToggleAllPageRowsSelectedProps() : {})} />
      </div>
    ),
    Cell: ({ row }) => (
      <div>
        <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
      </div>
    ),
  };

  const columns = React.useMemo(() => (readOnly ? TABLE_COLUMNS : [SELECTION_COLUMN, ...TABLE_COLUMNS]), [readOnly]);

    const tableInstance = useTable(
    {
      columns,
      data: useMemo(() => (unclaimedAwards ? unclaimedAwards?.results : []), [unclaimedAwards]),
      initialState: {
        pageIndex: 0,
        pageSize: queryPageSize,
      },
      manualPagination: true,
      pageCount: Math.ceil(unclaimedAwards?.count / queryPageSize),
      autoResetSortBy: false,
      autoResetExpanded: false,
      autoResetPage: false,
    },
    useSortBy,
    usePagination,
    useRowSelect
  );  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    page,
    pageCount,
    pageOptions,
    gotoPage,
    previousPage,
    canPreviousPage,
    nextPage,
    canNextPage,
    setPageSize,
    selectedFlatRows,
    state: { pageIndex, pageSize },
  } = tableInstance;
  

  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleRejectClick = () => {
    if (!selectedFlatRows || selectedFlatRows.length === 0) {
      dispatch(sendFlashMessage("Please select at least one award to reject", "alert-danger", "top"));
      return;
    }
    setConfirmOpen(true);
  };

  const confirmReject = async () => {
    setConfirmOpen(false);
    if (!selectedFlatRows || selectedFlatRows.length === 0) return;
    
    const claimIds = selectedFlatRows.map((r) => r.original.id);

    try {
      setLoading(true);
      const url = `/organization/${organization.id}/program/${program.id}/unclaimed-awards/reject`;
      const response = await axios.post(url, { claim_award_id: claimIds });
      
      if (response.data === true) {
        dispatch(sendFlashMessage("Selected awards were successfully rejected", "alert-success", "top"));
      }

      // refresh table data for current page
      const currentPage = pageIndex;
      const items = await apiTableService.fetchData({
        url: `/organization/${organization.id}/program/${program.id}/unclaimed-awards`,
        size: queryPageSize,
        page: currentPage,
      });
      if (items && items.results?.length === 0 && currentPage > 1) {
        // If no results and we're not on first page, fetch previous page
        const prevPageItems = await apiTableService.fetchData({
          url: `/organization/${organization.id}/program/${program.id}/unclaimed-awards`,
          size: queryPageSize,
          page: currentPage - 1,
        });
        if (prevPageItems) {
          setUnclaimedAwards(prevPageItems);
          gotoPage(currentPage - 2); // -2 because pageIndex is 0-based
        }
      } else if (items) {
        setUnclaimedAwards(items);
      }

      console.log(unclaimedAwards ? unclaimedAwards.count : 0);

      // clear selection (toggle all off) if available
      if (tableInstance && typeof tableInstance.toggleAllRowsSelected === "function") {
        tableInstance.toggleAllRowsSelected(false);
      }
    } catch (err) {
      console.error("Error rejecting selected awards:", err);
      dispatch(sendFlashMessage("Failed to reject selected awards", "alert-danger", "top"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    if (organization?.id && program?.id) {
      apiTableService
        .fetchData({
          url: `/organization/${organization.id}/program/${program.id}/unclaimed-awards`,
          size: queryPageSize,
          page: pageIndex,
        })
        .then((items) => {
          if (mounted && items) {
            setUnclaimedAwards(items);
            setLoading(false);
            console.log(items);
          }
        })
        .catch((err) => {
          console.log(err);
          setLoading(false);
        });
    }
    return () => (mounted = false);
  }, [organization, program, pageIndex]);

  if (loading && !unclaimedAwards) {
    return (
      <div style={{ padding: "20px 0px" }}>
        <TableSkeleton rows={3} columns={8} width={"100%"} height={15} />
      </div>
    );
  }

  const manualPageSize = [];
  
  return (
    <div className="users">
      <div className="header d-flex  justify-content-between">
        <div className="d-flex w-30 justify-content-between dropdown-group">
          
          <CanEdit>
            <Button color="primary" onClick={handleRejectClick} disabled={loading}>
              {loading ? <CircularProgress size={16} /> : "Reject Selected Awards"}
            </Button>
          </CanEdit>
        </div>
      </div>
      <Modal isOpen={confirmOpen} toggle={() => setConfirmOpen(!confirmOpen)}>
        <ModalHeader toggle={() => setConfirmOpen(false)}>Confirm Reject</ModalHeader>
        <ModalBody>
          Are you sure you want to reject {selectedFlatRows?.length || 0} selected award(s)?
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button color="danger" onClick={confirmReject}>Yes, Reject</Button>
        </ModalFooter>
      </Modal>
      <div className="points-summary-table">
        <Table striped borderless size="md" className="table--stack-mobile" {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                    {column.render("Header")}
                    <span>
                      {column.isSorted ? (column.isSortedDesc ? " 🔽" : " 🔼") : ""}
                    </span>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.map((row) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()}>
                  {row.cells.map((cell) => {
                    return (
                      <td
                        {...cell.getCellProps()}
                        data-label={typeof cell.column.Header === "string" ? cell.column.Header : ""}
                      >
                        {cell.render("Cell")}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
      <div className="my-3 status">
        {rows.length > 0 && (
          <>
            
            <ReactTablePagination
              page={page}
              gotoPage={gotoPage}
              previousPage={previousPage}
              nextPage={nextPage}
              canPreviousPage={canPreviousPage}
              canNextPage={canNextPage}
              pageOptions={pageOptions}
              pageSize={pageSize}
              pageIndex={pageIndex}
              pageCount={pageCount}
              setPageSize={setPageSize}
              manualPageSize={manualPageSize}
              dataLength={unclaimedAwards ? unclaimedAwards.count : 0}
            />
          </>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    program: state.program,
    organization: state.organization,
  };
};

export default connect(mapStateToProps)(UnclaimedAwardsTable);
